import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  PlayIcon,
  ChartBarIcon,
  BellIcon,
  CreditCardIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const ProductPreviewSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: 'بطاقة التسويق الذكية',
      description: 'هكذا تقرر ميزانية التسويق في 10 ثوانٍ فقط',
      icon: ChartBarIcon,
      gradient: 'from-blue-500 to-cyan-500',
      features: [
        'تحليل أداء جميع القنوات',
        'توصيات AI لتوزيع الميزانية',
        'مقارنة ROAS بين الحملات',
        'تنبيهات الأداء الضعيف'
      ]
    },
    {
      id: 2,
      title: 'تنبيه تأخير الرواتب',
      description: 'اكتشاف تلقائي لتأخير رفع ملف مدد',
      icon: BellIcon,
      gradient: 'from-red-500 to-pink-500',
      features: [
        'مراقبة مواعيد الرواتب',
        'تنبيهات استباقية',
        'تقارير امتثال جاهزة',
        'تكامل مباشر مع مدد'
      ]
    },
    {
      id: 3,
      title: 'تحليل مقارن للفروع',
      description: 'مقارنة أداء فروعك في لوحة واحدة',
      icon: CreditCardIcon,
      gradient: 'from-green-500 to-emerald-500',
      features: [
        'أداء كل فرع منفصل',
        'مقارنة بالأهداف المحددة',
        'تحليل الربحية',
        'توصيات التحسين'
      ]
    },
    {
      id: 4,
      title: 'خصومات الشراكات',
      description: 'خصم 20% تلقائي على Google Ads',
      icon: TagIcon,
      gradient: 'from-purple-500 to-indigo-500',
      features: [
        'تفعيل تلقائي للخصومات',
        'شراكات حصرية',
        'توفير آلاف الريالات',
        'تقارير الوفورات'
      ]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            شاهد المنصة في العمل
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            اكتشف كيف تحول FLYN بيانات شركتك إلى رؤى قابلة للتنفيذ
          </p>
        </motion.div>

        <div className="relative">
          {/* Main Carousel */}
          <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="flex flex-col lg:flex-row min-h-[600px]"
              >
                {/* Content Side */}
                <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="mb-8">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${slides[currentSlide].gradient} p-4 shadow-lg mb-6`}>
                      {React.createElement(slides[currentSlide].icon, { className: "w-8 h-8 text-white" })}
                    </div>
                    
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      {slides[currentSlide].title}
                    </h3>
                    
                    <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                      {slides[currentSlide].description}
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4">
                    {slides[currentSlide].features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex items-center space-x-3 rtl:space-x-reverse"
                      >
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${slides[currentSlide].gradient}`}></div>
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="mt-8 inline-flex items-center px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 w-fit"
                  >
                    <PlayIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
                    جرب هذه الميزة
                  </motion.button>
                </div>

                {/* Visual Side */}
                <div className="flex-1 p-8 lg:p-12 flex items-center justify-center bg-gray-50 dark:bg-gray-700">
                  <div className="relative w-full max-w-md">
                    {/* Mock Dashboard Card */}
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="glass-card p-6 rounded-xl shadow-xl"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {slides[currentSlide].title}
                        </h4>
                        <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${slides[currentSlide].gradient}`}></div>
                      </div>
                      
                      {/* Mock Chart */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">الأداء</span>
                          <span className="text-sm font-semibold text-green-600">+24%</span>
                        </div>
                        
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: '75%' }}
                            transition={{ duration: 1, delay: 0.5 }}
                            className={`h-2 rounded-full bg-gradient-to-r ${slides[currentSlide].gradient}`}
                          ></motion.div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {currentSlide === 0 ? '4.2x' : currentSlide === 1 ? '3 أيام' : currentSlide === 2 ? '85%' : '20%'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {currentSlide === 0 ? 'ROAS' : currentSlide === 1 ? 'تأخير' : currentSlide === 2 ? 'كفاءة' : 'خصم'}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {currentSlide === 0 ? '45 ألف ر.س' : currentSlide === 1 ? '12' : currentSlide === 2 ? '120 ألف ر.س' : '8 آلاف ر.س'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {currentSlide === 0 ? 'إيراد' : currentSlide === 1 ? 'موظف' : currentSlide === 2 ? 'ربح' : 'وفورات'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Floating Elements */}
                    <motion.div
                      animate={{
                        y: [0, -10, 0],
                        rotate: [0, 5, 0],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-secondary rounded-full opacity-60"
                    />
                    <motion.div
                      animate={{
                        y: [0, 10, 0],
                        rotate: [0, -3, 0],
                      }}
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: 1,
                      }}
                      className="absolute -bottom-6 -left-6 w-6 h-6 bg-gradient-primary rounded-full opacity-40"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 rtl:left-auto rtl:right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
            >
              <ChevronLeftIcon className="w-6 h-6 text-gray-600 dark:text-gray-300 group-hover:text-primary-600 rtl:rotate-180" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 rtl:right-auto rtl:left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
            >
              <ChevronRightIcon className="w-6 h-6 text-gray-600 dark:text-gray-300 group-hover:text-primary-600 rtl:rotate-180" />
            </button>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center space-x-2 rtl:space-x-reverse mt-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentSlide
                    ? 'bg-primary-600 w-8'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductPreviewSection;
